import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setJobId } from "../redux/slices/jobIdSlice";
import { checkoutJob, checkInJob, setCheckoutStateFromJobData } from "../redux/slices/checkoutSlice";
import { selectUserDetails, selectUserRoles } from "../redux/slices/userSlice";
import { selectApiUrls } from "../redux/slices/apiUrlSlice";
import { clearJobDetails, selectJobDetails } from "../redux/slices/jobDetailsSlice";
import { clearApiStatus } from "../redux/slices/apiStatusSlice";
import { useEffect, useRef, useCallback } from "react";
import { fetchFullJob, syncJobData } from "../redux/slices/fullJobFetchSlice";
import { setZones, clearZones } from "../redux/slices/zoneCountSlice";
import { setReadOnly } from "../redux/slices/readOnlySlice";
import { fetchAllCriticalData } from "../redux/slices/criticalDataSlice";
import { mapUserRoles } from "./useRoleMapping";
import { PRODUCTION_PORTAL_PATH } from "../config/productionRoutes";
import useReadonlyJobRealtime from '../realtime/useReadonlyJobRealtime';
import useGeneralProjectRealtime from '../realtime/useGeneralProjectRealtime';
import { acquireJobInitialization } from './jobInitialization';

const normalizeUserName = (value) => String(value || "").trim().toLowerCase();
const FULL_JOB_TIMEOUT_MS = 20000;
const JOB_TRACE_KEY = "debugElecLoadTrace";

const isTraceEnabled = () => {
  if (process.env.NODE_ENV !== "development") {
    return false;
  }

  try {
    return window.localStorage.getItem(JOB_TRACE_KEY) === "1";
  } catch {
    return false;
  }
};

const traceLog = (message, payload) => {
  if (!isTraceEnabled()) {
    return;
  }

  if (payload !== undefined) {
    console.log(`[JobTrace] ${message}`, payload);
    return;
  }

  console.log(`[JobTrace] ${message}`);
};

const withTimeout = (promise, timeoutMs, timeoutMessage) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    }),
  ]);

const isJobDetailPath = (path = "") => /^\/\d+\/(generalInfo|salesInfo|mechInfo|elecInfo|changeLog|jobQa|additionalItems|forms(?:\/(?:apps|panel-shop|ul))?|changeOrders)$/.test(path);

/**
 * Custom hook to sync job data from API with Redux store
 * Compares API response with current Redux state and updates only changed fields
 * @returns {Function} syncJob - Function to trigger sync for current job
 */
export const useJobSync = () => {
  const dispatch = useDispatch();
  const jobId = useSelector((state) => state.jobId.jobId);
  const syncing = useSelector((state) => state.fullJobFetch.syncing);
  const lastSyncResult = useSelector((state) => state.fullJobFetch.lastSyncResult);
  
  const syncJob = useCallback(({ reuseRecent = false } = {}) => {
    if (!jobId) {
      console.warn("useJobSync: No jobId available for sync");
      return Promise.resolve(null);
    }

    const state = dispatch((innerDispatch, getState) => getState());
    const fullJobLoading = Boolean(state?.fullJobFetch?.loading);
    const syncInProgress = Boolean(state?.fullJobFetch?.syncing);

    if (fullJobLoading || syncInProgress) {
      traceLog("useJobSync skipped (full job load/sync in progress)", {
        jobId,
        fullJobLoading,
        syncing: syncInProgress,
      });
      return Promise.resolve(null);
    }
    
    return dispatch(syncJobData({ jobId, reuseRecent }))
      .unwrap()
      .then((result) => {
        return result;
      })
      .catch((error) => {
        console.error("useJobSync: Sync failed", error);
        throw error;
      });
  }, [dispatch, jobId]);
  
  return { syncJob, syncing, lastSyncResult };
};

const useJobHandler = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const urlJobId = Number(useParams().id);
  const jobId = useSelector((state) => state.jobId.jobId);
  const checkoutJobId = useSelector((state) => state.checkout.jobId); // Track checked out job
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const customRoles = useSelector((state) => state.customRoles?.roles || {});
  const isReadOnly = useSelector((state) => Boolean(state.readOnly?.isReadOnly));
  const apiUrls = useSelector(selectApiUrls);
  const currentUser = userDetails?.name;
  const jobDetails = useSelector(selectJobDetails);
  const criticalDataInitialized = useSelector((state) => Boolean(state.criticalData?.initialized));
  const criticalData = useSelector((state) => state.criticalData?.data);
  const mappedRoles = mapUserRoles(
    userRoles || [],
    userDetails?.username,
    { customRoles: { roles: customRoles } }
  );
  const canCheckout = Boolean(mappedRoles.Admin || mappedRoles.Apps || mappedRoles.Engineer || mappedRoles.PSN);
  
  // Track which job IDs we've already processed to prevent re-checkout on tab navigation
  const processedJobRef = useRef(null);
  
  // Only check if generalProject has data, not the entire jobDetails object
  const hasGeneralProject = jobDetails.generalProject && jobDetails.generalProject.length > 0;
  const loadedGeneralProjectId = hasGeneralProject
    ? Number(jobDetails.generalProject[0]?.ID ?? jobDetails.generalProject[0]?.ProjectID)
    : null;
  const isLoadedGeneralProjectForUrlJob = hasGeneralProject && loadedGeneralProjectId === urlJobId;
  useReadonlyJobRealtime(urlJobId, isLoadedGeneralProjectForUrlJob);
  useGeneralProjectRealtime({ jobId: urlJobId, enabled: !isReadOnly && isLoadedGeneralProjectForUrlJob });
  const hasRequiredCriticalData =
    criticalData?.dropdownValues !== null &&
    criticalData?.hoverDescriptions !== null &&
    criticalData?.globalVariables !== null;

  useEffect(() => {
    traceLog("useJobHandler state snapshot", {
      urlJobId,
      jobId,
      checkoutJobId,
      currentUser,
      hasGeneralProject,
      loadedGeneralProjectId,
      isLoadedGeneralProjectForUrlJob,
      processedJobRef: processedJobRef.current,
    });
  }, [urlJobId, jobId, checkoutJobId, currentUser, hasGeneralProject, loadedGeneralProjectId, isLoadedGeneralProjectForUrlJob]);

  const handleFullJobLoadFailure = useCallback((error, failedJobId) => {
    console.error(`Job load failed for ${failedJobId}:`, error);
    traceLog("handleFullJobLoadFailure", {
      failedJobId,
      error: error?.message || error,
    });
    dispatch(setReadOnly(true));
    const storedRedirect = localStorage.getItem("postAuthRedirectUrl");
    if (storedRedirect && isJobDetailPath(storedRedirect)) {
      localStorage.removeItem("postAuthRedirectUrl");
    }
    navigate(PRODUCTION_PORTAL_PATH, { replace: true });
  }, [dispatch, navigate]);

  const ensureCriticalDataLoaded = useCallback(async () => {
    if (criticalDataInitialized && hasRequiredCriticalData) {
      return;
    }

    traceLog("critical data preload start", {
      criticalDataInitialized,
      hasRequiredCriticalData,
      urlJobId,
    });

    try {
      await withTimeout(
        dispatch(fetchAllCriticalData({ forceRefresh: false })).unwrap(),
        FULL_JOB_TIMEOUT_MS,
        `critical data preload timed out after ${FULL_JOB_TIMEOUT_MS}ms`
      );
      traceLog("critical data preload success", { urlJobId });
    } catch (error) {
      console.warn("JobHandler: Critical data preload failed, continuing with job load", error);
      traceLog("critical data preload failed", {
        urlJobId,
        error: error?.message || error,
      });
    }
  }, [criticalDataInitialized, hasRequiredCriticalData, dispatch, urlJobId]);

  useEffect(() => {
    // Don't run if urlJobId is invalid
    if (!urlJobId || isNaN(urlJobId)) {
      return;
    }
    const initializationKey = JSON.stringify([urlJobId, apiUrls.fullJob, userDetails?.localAccountId || currentUser]);

    if (jobId === urlJobId) {
      // Same route/state job - fetch when data is missing OR belongs to a different job
      if (!hasGeneralProject || !isLoadedGeneralProjectForUrlJob) {
        const releaseInitialization = acquireJobInitialization(dispatch, initializationKey);
        if (!releaseInitialization) return;
        const loadSameJobData = async () => {
          traceLog("same job path: data missing, fetchFullJob start", { urlJobId });
          await ensureCriticalDataLoaded();

          // Clear stale job detail state before fetching the correct job payload
          dispatch(clearJobDetails());
          dispatch(clearApiStatus());

          try {
            const result = await withTimeout(
              dispatch(fetchFullJob({ jobId: urlJobId })).unwrap(),
              FULL_JOB_TIMEOUT_MS,
              `fetchFullJob timed out after ${FULL_JOB_TIMEOUT_MS}ms`
            );
            traceLog("same job path: fetchFullJob success", { urlJobId, result });
          } catch (error) {
            handleFullJobLoadFailure(error, urlJobId);
          }
        };

        loadSameJobData().finally(releaseInitialization);
      } else {
        traceLog("same job path: data already loaded, skipping fetch", { urlJobId });
        const existingGeneralProject = jobDetails.generalProject?.[0];
        if (existingGeneralProject) {
          dispatch(
            setCheckoutStateFromJobData({
              jobData: existingGeneralProject,
              currentUser,
            })
          );

          if (existingGeneralProject?.Zones !== undefined) {
            dispatch(setZones(existingGeneralProject.Zones));
          }
        }
      }
    } else {
      const releaseInitialization = acquireJobInitialization(dispatch, initializationKey);
      if (!releaseInitialization) return;
      // Different job - check in previous job if needed, then setup new job
      const checkInAndSetupNewJob = async () => {
        traceLog("different job path: setup start", {
          fromJobId: jobId,
          toJobId: urlJobId,
          checkoutJobId,
        });

        // Check in the previous job if there's one checked out
        if (checkoutJobId && checkoutJobId !== urlJobId) {
          try {
            await dispatch(checkInJob({ jobId: checkoutJobId, currentUser })).unwrap();
            console.log(`Checked in previous job ${checkoutJobId} before loading job ${urlJobId}`);
            traceLog("checkIn previous job success", { checkoutJobId, urlJobId });
          } catch (checkInError) {
            console.warn(`Failed to check in previous job ${checkoutJobId}:`, checkInError);
            traceLog("checkIn previous job failed", {
              checkoutJobId,
              urlJobId,
              error: checkInError?.message || checkInError,
            });
            // Continue with new job setup even if check-in failed
          }
        }
        
        // Clear old data and setup new job
        dispatch(clearJobDetails());
        dispatch(clearZones());  
        dispatch(setJobId(urlJobId));   
        dispatch(clearApiStatus());
        
        // Wait for Redux state to fully clear before fetching new data
        // This prevents any potential race conditions or data mixing

        await ensureCriticalDataLoaded();

        // IMPORTANT: Await checkout completion before fetching data
        // This prevents race conditions between checkout API and fetchFullJob
        // Check if we've already processed this job to avoid re-checkout
        const alreadyProcessed = processedJobRef.current === urlJobId;
        let shouldCheckout = !alreadyProcessed && canCheckout && !isReadOnly;
        traceLog("checkout decision pre-fetch", { urlJobId, alreadyProcessed, shouldCheckout });
        
        // First, fetch the job data to check checkout status
        try {
          await withTimeout(
            dispatch(fetchFullJob({ jobId: urlJobId })).unwrap(),
            FULL_JOB_TIMEOUT_MS,
            `fetchFullJob timed out after ${FULL_JOB_TIMEOUT_MS}ms`
          );
          traceLog("different job path: fetchFullJob success", { urlJobId });
          
          // After fetching, check if we need to checkout
          // Re-get the generalProject data after fetch
          const state = dispatch((dispatch, getState) => getState());
          const updatedJobDetails = state.jobDetails?.jobDetails;
          const updatedGeneralProject = updatedJobDetails?.generalProject?.[0];
          
          // Populate zone count from fetched data
          if (updatedGeneralProject?.Zones !== undefined) {
            dispatch(setZones(updatedGeneralProject.Zones));
          }

          if (updatedGeneralProject) {
            dispatch(
              setCheckoutStateFromJobData({
                jobData: updatedGeneralProject,
                currentUser,
              })
            );
          }
          
          if (updatedGeneralProject?.IsCheckedOut === true) {
            if (normalizeUserName(updatedGeneralProject?.CheckedOutBy) === normalizeUserName(currentUser)) {
              shouldCheckout = false;
              dispatch(setReadOnly(false));
            } else {
              shouldCheckout = false; // Don't attempt checkout if someone else has it
              dispatch(setReadOnly(true));
            }
          }

          traceLog("post-fetch checkout evaluation", {
            urlJobId,
            shouldCheckout,
            isCheckedOut: updatedGeneralProject?.IsCheckedOut,
            checkedOutBy: updatedGeneralProject?.CheckedOutBy,
            currentUser,
            zones: updatedGeneralProject?.Zones,
          });
        } catch (fetchError) {
          handleFullJobLoadFailure(fetchError, urlJobId);
          return;
        }
        
        // Only checkout if needed and not already processed
        if (shouldCheckout) {
          try {
            const checkoutResult = await dispatch(checkoutJob({ jobId: urlJobId, currentUser })).unwrap();
            // Mark this job as processed after successful checkout
            processedJobRef.current = urlJobId;
            dispatch(setReadOnly(false));
            traceLog("checkout success", { urlJobId, checkoutResult });
          } catch (checkoutError) {
            // Checkout failed, but still allow viewing (read-only mode)
            console.warn("Checkout failed:", checkoutError);
            dispatch(setReadOnly(true));
            traceLog("checkout failed", { urlJobId, error: checkoutError?.message || checkoutError });
          }
        } else {
          traceLog("checkout skipped", { urlJobId });
        }
      };

      checkInAndSetupNewJob().finally(releaseInitialization);
    }
  }, [jobId, dispatch, navigate, urlJobId, currentUser, userDetails?.localAccountId, apiUrls, hasGeneralProject, isLoadedGeneralProjectForUrlJob, checkoutJobId, handleFullJobLoadFailure, ensureCriticalDataLoaded, canCheckout, isReadOnly, jobDetails.generalProject]);
};

export default useJobHandler;
